import type { Currency, ExpenseKind } from '@kmdx/contracts';
import { Camera, Check, Delete } from 'lucide-react';
import { useState } from 'react';
import { CaptureFlow } from '../components/capture-flow';
import { Banner, BigButton, Card, Label, Screen } from '../components/ui';
import { saveExpense } from '../lib/actions';
import { db, useLive } from '../lib/db';
import { money } from '../lib/format';
import {
  COMMON_COUNTRIES,
  countryName,
  DRIVER_CURRENCIES,
  DRIVER_EXPENSE_KINDS,
  EXPENSE_LABELS,
} from '../lib/labels';
import { nextStopOf, useActiveTrips, useStops } from '../lib/selectors';

/**
 * Непредвиден разход на три докосвания: какво → колко → запиши. Държавата и
 * валутата се налучкват от текущия адрес, защото шофьорът плаща там, където е.
 *
 * Горивото, пътните такси, винетките, AdBlue и фериботите НЕ минават оттук —
 * плащат се със служебната карта във всеки бус (виж `DRIVER_EXPENSE_KINDS`).
 *
 * Държавата се пази задължително — по нея се възстановява чуждият ДДС по
 * Директива 2008/9/ЕО (срок: 30 септември на следващата година).
 */
export function ExpenseScreen() {
  const trips = useActiveTrips();
  const trip = trips[0] ?? null;
  const stops = useStops(trip?.id ?? null);
  const nextStop = nextStopOf(stops);

  const [kind, setKind] = useState<ExpenseKind | null>(null);
  const [amount, setAmount] = useState('');
  const [ccy, setCcy] = useState<Currency>('EUR');
  /**
   * Държавата се избира от следващия адрес — но той се зарежда след първото
   * рисуване. Затова изборът е празен, докато шофьорът не натисне, и дотогава
   * важи държавата на следващия адрес, а не винаги България (от нея зависи
   * възстановяването на чуждото ДДС).
   */
  const [chosenCountry, setCountry] = useState<string | null>(null);
  const country = chosenCountry ?? nextStop?.country ?? 'BG';
  const [receiptDocId, setReceiptDocId] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [saved, setSaved] = useState(false);

  const recent = useLive(
    async () => {
      const rows = await db.expenses.toArray();
      return rows.sort((a, b) => b.spentAt.localeCompare(a.spentAt)).slice(0, 8);
    },
    [],
    [],
  );

  const amountMinor = Math.round(Number(amount.replace(',', '.')) * 100);
  const valid = kind !== null && Number.isFinite(amountMinor) && amountMinor > 0;

  async function submit() {
    if (!kind || !valid) return;
    await saveExpense({
      tripId: trip?.id ?? null,
      kind,
      amountMinor,
      ccy,
      country,
      receiptDocId,
    });
    setKind(null);
    setAmount('');
    setReceiptDocId(null);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 4000);
  }

  if (!kind) {
    return (
      <Screen title="Непредвиден разход" logo>
        {saved ? <Banner tone="go">Разходът е записан.</Banner> : null}
        <p className="text-xl text-muted">Какво плати?</p>
        <p className="mb-3 text-base text-muted">
          Горивото, пътните такси и винетките са на служебната карта — тук не се въвеждат.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {DRIVER_EXPENSE_KINDS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setKind(item)}
              className="rounded-2xl border-2 border-line bg-card px-3 py-6 text-2xl font-bold"
              style={{ minHeight: 96 }}
            >
              {EXPENSE_LABELS[item]}
            </button>
          ))}
        </div>

        {recent.length > 0 ? (
          <div className="mt-6">
            <Label>Последни</Label>
            <ul className="mt-2 space-y-2">
              {recent.map((expense) => (
                <li
                  key={expense.id}
                  className="flex items-center justify-between rounded-xl border-2 border-line px-3 py-2 text-lg"
                >
                  <span className="font-semibold">{EXPENSE_LABELS[expense.kind]}</span>
                  <span>
                    {money(expense.amountMinor, expense.ccy)} · {countryName(expense.country)}
                  </span>
                  <span className="text-muted">
                    {expense.synced === 1 ? 'изпратен' : 'чака'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Screen>
    );
  }

  return (
    <Screen title={EXPENSE_LABELS[kind]} back>
      <div className="space-y-4">
        <Card className="!bg-paper">
          <Label>Сума</Label>
          <div className="text-right text-5xl font-bold tabular-nums">
            {amount === '' ? '0' : amount} <span className="text-3xl">{ccy}</span>
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() =>
                setAmount((value) => {
                  if (key === ',' && value.includes(',')) return value;
                  const next = `${value}${key}`;
                  const decimals = next.split(',')[1];
                  return decimals && decimals.length > 2 ? value : next;
                })
              }
              className="rounded-2xl border-2 border-line bg-card text-3xl font-bold active:bg-line"
              style={{ minHeight: 76 }}
            >
              {key}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setAmount((value) => value.slice(0, -1))}
            aria-label="Изтрий"
            className="flex items-center justify-center rounded-2xl border-2 border-line bg-card active:bg-line"
            style={{ minHeight: 76 }}
          >
            <Delete size={30} />
          </button>
        </div>

        <div>
          <Label>Валута</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {DRIVER_CURRENCIES.map((item) => (
              <Chip key={item} active={item === ccy} onClick={() => setCcy(item)}>
                {item}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <Label>Държава на плащане</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {COMMON_COUNTRIES.map((code) => (
              <Chip key={code} active={code === country} onClick={() => setCountry(code)}>
                {countryName(code)}
              </Chip>
            ))}
          </div>
        </div>

        <BigButton
          tone={receiptDocId ? 'plain' : 'brand'}
          icon={receiptDocId ? <Check size={26} /> : <Camera size={26} />}
          onClick={() => setCapturing(true)}
        >
          {receiptDocId ? 'Бележката е заснета' : 'Снимай бележката'}
        </BigButton>

        <BigButton tone="go" disabled={!valid} onClick={() => void submit()}>
          Запиши разхода
        </BigButton>
      </div>

      {capturing ? (
        /* Бележки за гориво и пътни такси вече не идват оттук — всяка бележка е „друг документ". */
        <CaptureFlow
          type="other"
          title="Касова бележка"
          tripId={trip?.id ?? null}
          orderStopId={null}
          multiPage={false}
          hint="Сложи цялата бележка в рамката — сумата и ДДС-то трябва да се четат."
          checklist={['Цялата бележка е в кадъра', 'Сумата и ДДС се четат']}
          onClose={() => setCapturing(false)}
          onDone={(result) => {
            const photo = result.photos[0];
            if (photo) setReceiptDocId(photo.id);
            setCapturing(false);
          }}
        />
      ) : null}
    </Screen>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border-2 px-4 py-3 text-lg font-semibold ${
        active ? 'border-go bg-go text-white' : 'border-line bg-card'
      }`}
      style={{ minHeight: 56 }}
    >
      {children}
    </button>
  );
}
